import { useCallback, useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";

export const BeforeAfter = ({ before, after, beforeAlt, afterAlt }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let second;
    const first = animate(50, 66, {
      duration: 1.5,
      delay: 0.9,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => !dragging.current && setPos(v),
    });
    first.then(() => {
      second = animate(66, 50, {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => !dragging.current && setPos(v),
      });
    });
    return () => {
      first.stop();
      second?.stop();
    };
  }, [inView]);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(94, Math.max(6, p)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      role="slider"
      aria-label="Before and after cleaning comparison — drag or use arrow keys"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(6, p - 4));
        if (e.key === "ArrowRight") setPos((p) => Math.min(94, p + 4));
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      className="relative aspect-[4/5] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-[2.5rem] shadow-2xl shadow-sky-900/20 ring-1 ring-slate-100 focus-visible:outline-emerald-500"
    >
      <img
        src={after}
        alt={afterAlt}
        draggable={false}
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={beforeAlt}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      <span className="absolute left-5 top-5 rounded-full bg-navy-800/85 px-4 py-1.5 font-heading text-xs font-bold uppercase tracking-[0.15em] text-white backdrop-blur">
        Before
      </span>
      <span className="absolute right-5 top-5 rounded-full bg-emerald-500/90 px-4 py-1.5 font-heading text-xs font-bold uppercase tracking-[0.15em] text-white backdrop-blur">
        After
      </span>

      <div
        className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.35)]"
        style={{ left: `${pos}%` }}
        aria-hidden="true"
      >
        <span className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy-800 shadow-xl ring-1 ring-slate-200">
          <ChevronsLeftRight className="h-5 w-5" />
        </span>
      </div>

    </div>
  );
};
